import fs from 'node:fs'
import path from 'node:path'
import { readProjectPackageJson } from '@next-adapter/package-json/read-project-package-json.js'
import { parseDocument } from 'yaml'
import { findPnpmWorkspacePath } from './find-pnpm-workspace-path.js'
import { getPnpmPackageExtensionsToEnsure } from './get-pnpm-package-extensions-to-ensure.js'

export function ensurePnpmPackageExtensions(cwd: string, dependencies: string[]): void {
  const pkgPath = path.join(cwd, 'package.json')
  const workspacePath = findPnpmWorkspacePath(cwd)
  const pkg = readProjectPackageJson(cwd)
  if (!pkg) {
    return
  }

  const extensionsToEnsure = getPnpmPackageExtensionsToEnsure(pkg, dependencies)
  if (Object.keys(extensionsToEnsure).length === 0) {
    return
  }

  const workspaceContent = fs.existsSync(workspacePath)
    ? fs.readFileSync(workspacePath, 'utf-8')
    : '{}\n'
  const workspaceDocument = parseDocument(workspaceContent)
  if (workspaceDocument.errors.length > 0) {
    throw new Error(
      `Could not update pnpm-workspace.yaml: ${workspaceDocument.errors[0]?.message ?? 'invalid YAML'}`
    )
  }

  let workspaceChanged = false
  let packageJsonChanged = false

  for (const [selector, extension] of Object.entries(extensionsToEnsure)) {
    const legacyExtension = pkg.pnpm?.packageExtensions?.[selector]

    for (const section of ['dependencies', 'peerDependencies'] as const) {
      for (const [name, version] of Object.entries(legacyExtension?.[section] ?? {})) {
        const valuePath = ['packageExtensions', selector, section, name]
        if (workspaceDocument.getIn(valuePath) === undefined) {
          workspaceDocument.setIn(valuePath, version)
          workspaceChanged = true
        }
      }
    }

    for (const [name, version] of Object.entries(extension.dependencies ?? {})) {
      const valuePath = ['packageExtensions', selector, 'dependencies', name]
      if (workspaceDocument.getIn(valuePath) !== version) {
        workspaceDocument.setIn(valuePath, version)
        workspaceChanged = true
      }
    }

    const legacyPackageExtensions = pkg.pnpm?.packageExtensions
    if (legacyPackageExtensions && Object.hasOwn(legacyPackageExtensions, selector)) {
      delete legacyPackageExtensions[selector]
      packageJsonChanged = true
      if (Object.keys(legacyPackageExtensions).length === 0 && pkg.pnpm) {
        const { packageExtensions: _packageExtensions, ...remainingPnpmConfig } = pkg.pnpm
        pkg.pnpm = Object.keys(remainingPnpmConfig).length > 0 ? remainingPnpmConfig : undefined
      }
    }
  }

  if (workspaceChanged) {
    fs.writeFileSync(workspacePath, workspaceDocument.toString({ lineWidth: 0 }), 'utf-8')
  }

  if (packageJsonChanged) {
    fs.writeFileSync(pkgPath, `${JSON.stringify(pkg, null, 2)}\n`, 'utf-8')
  }
}
